import { useState, type PropsWithChildren, Children } from 'react';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import { useCellActions } from './useCellActions.js';
import * as styles from './UserActions.sty.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  DataTableV2ColumnContextProvider,
  useDataTableV2,
} from '../../contexts/DataTableV2Context.js';

/**
 * @internal
 */
export function InlineCellActionMenu(
  props: PropsWithChildren<{
    columnId: string;
    rowId: string;
    type: 'cell' | 'header';
    'aria-label': string;
    'data-header-row'?: number;
    'data-column'?: number;
    hovered?: boolean;
    onClose?: () => void;
  }>,
) {
  const {
    columnId,
    rowId,
    type,
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    hovered,
    onClose,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);

  const table = useDataTableV2();
  const { builtinActions, customActions } = useCellActions({
    enabled: menuOpen,
    rowId,
    columnId,
    type,
  });

  const triggerProps = {
    type: 'button',
    className: styles.actionButton,
    style: {
      display: menuOpen ? 'inline-block' : undefined,
    },
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    'data-trigger': 'useraction',
    children: <DotMenuIcon style={{ width: 20, height: 20 }} />,
  } as const;

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (!menuOpen) {
    // Omit the button from the default render to reduce the number of rendered elements.
    if (hovered === false) {
      return;
    }

    return (
      <button
        {...triggerProps}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setMenuOpen(true);
        }}
      />
    );
  }

  const column = table.getColumn(columnId);
  // Calculate the alignment of the opening menu based on the column alignment.
  const alignment = column?.columnDef.alignment?.includes('right')
    ? 'start'
    : 'end';

  return (
    <Menu
      open={menuOpen}
      onOpenChange={(open) => {
        if (!open && onClose) {
          onClose();
        }
        setMenuOpen(open);
      }}
    >
      <Menu.Trigger>
        <button
          {...triggerProps}
          onClick={(evt) => {
            evt.stopPropagation();
          }}
        />
      </Menu.Trigger>
      {menuOpen === true ? (
        <DataTableV2ColumnContextProvider value={column}>
          <Menu.Content alignment={alignment}>
            {builtinActions !== null && builtinActions?.length !== 0 ? (
              /*
             Run all actions through a Children map so they will receive
             an automatic key and do not violate react key map rules.
            */
              <Menu.Group>
                {Children.map(builtinActions, (child) => child)}
              </Menu.Group>
            ) : null}
            {/*
           Run all actions through a Children map so they will receive
           an automatic key and do not violate react key map rules.
           */}
            {Children.map(customActions, (child) => child)}
          </Menu.Content>
        </DataTableV2ColumnContextProvider>
      ) : null}
    </Menu>
  );
}
